// /app/frontend/src/pages/seo/SisterWifeDatingAppPage.jsx
// Product-positioning page — explains UnityLove's role for sister-wife seekers.
import React from 'react';
import PublicSEOHeader from '../../components/PublicSEOHeader';
import {
  SEOHead,
  organizationSchema,
  mobileAppSchema,
  buildBreadcrumbSchema,
  GradientHero,
  FeatureCard,
  RelatedGuides,
  BottomCTA,
} from './SEOUtils';

export default function SisterWifeDatingAppPage() {
  const path = '/sister-wife-dating-app';
  const schemas = [
    organizationSchema,
    mobileAppSchema,
    buildBreadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: 'Guides', url: '/guides' },
      { name: 'Sister Wife Dating App', url: path },
    ]),
  ];

  return (
    <div className="min-h-screen bg-white">
      <PublicSEOHeader />
      <SEOHead
        title="Sister Wife Dating App"
        description="UnityLove helps singles and couples explore meaningful connections, family-building, and sister-wife relationships with honesty and respect."
        canonical={path}
        schemas={schemas}
      />

      <GradientHero
        eyebrow="A Sister Wife Dating App"
        headline="A Dating App For Sister Wife Connections"
        sub="UnityLove is built for singles and couples who want family — not just casual dating. Connect with people who share your long-term intentions."
        body="No swiping past the basics. No hiding what you actually want. Every profile is set up to communicate intention clearly."
        primaryCta={{ label: 'Create Your Profile Free', to: '/auth?mode=signup' }}
        secondaryCta={{ label: 'How To Find A Sister Wife', to: '/how-to-find-a-sister-wife' }}
      />

      <main className="max-w-4xl mx-auto px-4 py-14">
        {/* Intro */}
        <section className="mb-12 max-w-2xl mx-auto text-center">
          <p className="text-lg text-slate-700 leading-relaxed">
            Most dating apps weren’t designed for the people in this space — couples seeking a third long-term partner, women open to joining established families, and singles dating with family-building in mind. UnityLove is.
          </p>
        </section>

        {/* Who it's for */}
        <section className="mb-14">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-6 text-center">
            Who UnityLove Is Built For
          </h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <FeatureCard
              icon="👥"
              accent="rose"
              title="Couples Seeking Partnership"
              body="Linked couple profiles, shared matching preferences, and conversations that include both partners from the start — never a surprise to either."
            />
            <FeatureCard
              icon="🌱"
              accent="emerald"
              title="Women Exploring Family-Building"
              body="A space to be open about long-term goals — children, household, shared faith or values — without filtering through years of casual swiping."
            />
            <FeatureCard
              icon="🗣️"
              accent="sky"
              title="Communication & Transparency"
              body="Profile fields, prompts, and structured conversations push everyone toward clarity early. No mixed signals about what people actually want."
            />
            <FeatureCard
              icon="🎯"
              accent="amber"
              title="Shared Expectations"
              body="Detailed values, family-vision, and relationship-style fields so you can find people whose end-state actually overlaps with yours."
            />
          </div>
        </section>

        {/* Why UnityLove section */}
        <section className="mb-14">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-4">
            Why UnityLove Is Different
          </h2>
          <div className="space-y-4 text-slate-700 leading-relaxed">
            <p>
              On mainstream apps, mentioning a sister-wife search usually gets you ghosted, fetishized, or reported. That hostility kills the conversation before it starts — and forces couples into awkward, dishonest workarounds just to find anyone who understands the model.
            </p>
            <p>
              UnityLove flips that. Every profile is welcomed in their full identity. Couples can match as a unit. Single women looking for family can say so without hiding it. The whole community is here for the same reason: building real relationships with people whose intentions match.
            </p>
            <p>
              We also believe deeply in safety. Identity verification (powered by Stripe Identity), profile reporting, and community moderation are all in place — because the slow, intentional pace of plural family-building only works in a space that takes authenticity seriously.
            </p>
          </div>
        </section>

        {/* Features bullet block */}
        <section className="mb-14 bg-slate-50 rounded-3xl p-6 sm:p-8">
          <h2 className="font-playfair text-2xl font-bold text-slate-900 mb-5">
            What You’ll Find Inside
          </h2>
          <ul className="grid gap-3 sm:grid-cols-2 text-slate-700">
            {[
              'Couple-linked profiles for couples seeking together',
              'Long-form profile prompts to communicate intention',
              'Identity verification for authentic connections',
              'Video lounges to meet face-to-face before in-person',
              'Themed conversation spaces for poly and plural family topics',
              'Reporting and moderation built around community safety',
            ].map((b) => (
              <li key={b} className="flex items-start gap-2.5">
                <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-rose-600 text-white text-xs font-bold">
                  ✓
                </span>
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Internal link block */}
        <section className="mb-4 rounded-2xl border border-slate-200 bg-white p-6">
          <h2 className="text-xl font-semibold text-slate-900 mb-2">
            Before You Start
          </h2>
          <p className="text-slate-700 mb-3">
            New to sister-wife dating? These guides cover the foundations:
          </p>
          <ul className="space-y-1.5 text-slate-700 list-disc list-inside">
            <li>
              <a href="/how-to-find-a-sister-wife" className="text-rose-600 hover:text-rose-700 font-medium">
                How To Find A Sister Wife
              </a>
            </li>
            <li>
              <a href="/ethical-polyamory-for-beginners" className="text-rose-600 hover:text-rose-700 font-medium">
                Ethical Polyamory For Beginners
              </a>
            </li>
            <li>
              <a href="/safe-poly-dating" className="text-rose-600 hover:text-rose-700 font-medium">
                Safe Poly Dating Guide
              </a>
            </li>
          </ul>
        </section>
      </main>

      <RelatedGuides currentPath={path} />
      <BottomCTA
        headline="Ready to build meaningful connections?"
        body="Create your profile on UnityLove and connect with singles, couples, and families who share your relationship goals."
      />
    </div>
  );
}
